import {Table} from "antd";
import className from "classnames/bind";
import {Loading} from "~/components";
import {useGetGscAnalysisQuery} from "~/reduxs/api/gscApiSlice";
import {TrendBadge} from "./GscTrend";
import style from "../style/Gsc.module.scss";

const cn = className.bind(style);
const fmt = (n) => Number(n || 0).toLocaleString('vi-VN');

/** Ô chênh lệch click tuyệt đối (+xanh / −đỏ). */
function DeltaCell({delta}) {
	const up = delta > 0;
	return <span style={{color: up ? '#16a34a' : '#dc2626', fontWeight: 600}}>{up ? '+' : ''}{fmt(delta)}</span>;
}

const PAGER = {pageSize: 10, hideOnSinglePage: true, showSizeChanger: false};

/**
 * Tab "Phân tích" của GSC — số liệu suy ra TỪ DB (không gọi Google):
 *  - Quick wins: từ khoá vị trí TB 5–15 (sát trang 1) + nhiều hiển thị → cơ hội lên top.
 *  - Trang / Từ khoá tăng & giảm click mạnh nhất so kỳ liền trước.
 */
function GscAnalysis({projectId, from, to}) {

	const {data, isFetching} = useGetGscAnalysisQuery({projectId, from, to});

	if (isFetching && !data) return <Loading />;
	if (!data) return <p className={cn('metaRow')}>Chưa có dữ liệu phân tích.</p>;

	const quickCols = [
		{title: 'Từ khoá', dataIndex: 'value', ellipsis: true},
		{title: 'Vị trí', dataIndex: 'position', width: 90, defaultSortOrder: 'ascend',
			sorter: (a, b) => a.position - b.position, render: (v) => Number(v).toFixed(1)},
		{title: 'Impressions', dataIndex: 'impressions', width: 120,
			sorter: (a, b) => a.impressions - b.impressions, render: fmt},
		{title: 'Clicks', dataIndex: 'clicks', width: 90, render: fmt},
		{title: 'CTR', dataIndex: 'ctr', width: 80, render: (v) => `${(v * 100).toFixed(1)}%`},
	];

	const wlCols = (label, isPage) => [
		{title: label, dataIndex: 'value', ellipsis: true,
			render: (v) => isPage ? <a href={v} target="_blank" rel="noreferrer">{v}</a> : <span>{v}</span>},
		{title: 'Clicks', dataIndex: 'clicks', width: 90, render: fmt},
		{title: 'Kỳ trước', dataIndex: 'prev_clicks', width: 90, render: fmt},
		{title: 'Thay đổi', dataIndex: 'delta', width: 100, render: (d) => <DeltaCell delta={d} />},
		{title: '% so kỳ trước', dataIndex: 'delta_pct', width: 120, render: (v) => <TrendBadge value={v} />},
	];

	const wlTable = (rows, label, isPage, empty) => (
		<Table rowKey="value" size="small" columns={wlCols(label, isPage)} dataSource={rows}
			pagination={PAGER} locale={{emptyText: empty}} />
	);

	return (
		<div>
			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Cơ hội nhanh (Quick wins)</h3>
				<p className={cn('metaRow')}>
					Từ khoá đang ở vị trí trung bình 5–15 (sát trang 1) với nhiều lượt hiển thị — tối ưu nội dung/tiêu đề
					hoặc thêm liên kết nội bộ để đẩy lên top.
				</p>
				<Table rowKey="value" size="small" columns={quickCols} dataSource={data.quick_wins || []}
					pagination={PAGER} locale={{emptyText: 'Chưa có từ khoá nào ở vị trí 5–15 trong khoảng này'}} />
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Trang tăng traffic</h3>
				{wlTable(data.pages?.rising || [], 'Trang', true, 'Không có trang nào tăng click so kỳ trước')}
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Trang giảm traffic</h3>
				{wlTable(data.pages?.falling || [], 'Trang', true, 'Không có trang nào giảm click so kỳ trước')}
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Từ khoá tăng</h3>
				{wlTable(data.queries?.rising || [], 'Từ khoá', false, 'Không có từ khoá nào tăng click so kỳ trước')}
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Từ khoá giảm</h3>
				{wlTable(data.queries?.falling || [], 'Từ khoá', false, 'Không có từ khoá nào giảm click so kỳ trước')}
			</div>
		</div>
	);
}

export default GscAnalysis;
